import React from 'react'

import { Calendar } from 'primereact/calendar'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'

import { PanelContainer } from '../../../../components/Panel'
import { getConfigTranslation } from '../../../../utils/helper/getConfigTranslation'
import { stringToDate } from '../../../../utils/helper/stringToDate'
import i18n from '../../../../utils/i18n'
import { ConfigurableComponent } from '../../../../utils/wrapper'
import { useConfigContext } from '../../../ui-config/context'
import { useOrder } from '../../data'
import { AllowanceCharges } from './AllowanceCharges'
import { TermsOfPayment } from './TermsOfPayment'

interface Props {
  id: string
}

export const OrderInformation: React.FC<Props> = ({ id }) => {
  const { data: order, isLoading } = useOrder(id)
  const { language } = i18n
  const { uiConfig: config } = useConfigContext()

  // ************************************************************
  // components
  // ************************************************************
  // orderId
  const orderIdLabel = <label className="text-sm" htmlFor="orderId"></label>

  const orderIdComponent = (
    <InputText
      id="orderId"
      value={order?.Payload.OrderId}
      className="w-full"
      disabled
    />
  )

  const orderIdWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.orderInformation}
      component={orderIdComponent}
      label={orderIdLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // orderDate
  const orderDateLabel = <label className="text-sm" htmlFor="orderDate"></label>

  const orderDateComponent = (
    <Calendar
      id="orderDate"
      value={stringToDate(`${order?.Payload.OrderDate}`)}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const orderDateWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.orderInformation}
      component={orderDateComponent}
      label={orderDateLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // orderDeliveryDate
  const orderDeliveryDateLabel = (
    <label className="text-sm" htmlFor="orderDeliveryDate"></label>
  )

  const orderDeliveryDateComponent = (
    <Calendar
      id="orderDeliveryDate"
      value={stringToDate(`${order?.Payload.OrderDeliveryDate}`)}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const orderDeliveryDateWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.orderInformation}
      component={orderDeliveryDateComponent}
      label={orderDeliveryDateLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // termsOfDelivery
  const termsOfDeliveryLabel = (
    <label className="text-sm" htmlFor="termsOfDelivery"></label>
  )

  const termsOfDeliveryComponent = (
    <div id="termsOfDelivery" className="p-inputgroup">
      <span className="p-inputgroup-addon">
        {order?.Payload.TermsOfDelivery?.Code}
      </span>
      <InputText
        value={order?.Payload.TermsOfDelivery?.Description}
        className="w-full"
        disabled
      />
    </div>
  )

  const termsOfDeliveryWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.orderInformation}
      component={termsOfDeliveryComponent}
      label={termsOfDeliveryLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // currency
  const currencyLabel = <label className="text-sm" htmlFor="currency"></label>

  const currencyComponent = (
    <InputText
      id="currency"
      value={order?.Payload.Currency}
      className="w-full"
      disabled
    />
  )

  const currencyWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.orderInformation}
      component={currencyComponent}
      label={currencyLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // totalNetItemAmount
  const totalNetItemAmountLabel = (
    <label className="text-sm" htmlFor="totalNetItemAmount"></label>
  )

  const totalNetItemAmountComponent = (
    <InputNumber
      id="totalNetItemAmount"
      value={order?.Payload.TotalNetItemAmount}
      mode="currency"
      currency={order?.Payload.Currency}
      locale={language}
      className="w-full"
      disabled
    />
  )

  const totalNetItemAmountWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.orderInformation}
      component={totalNetItemAmountComponent}
      label={totalNetItemAmountLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // notes
  const notesLabel = <label className="text-sm" htmlFor="notes"></label>

  const notesComponent = (
    <InputTextarea
      id="notes"
      value={order?.Payload.Notes}
      rows={5}
      className="w-full"
      disabled
    />
  )

  const notesWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.orderInformation}
      component={notesComponent}
      label={notesLabel}
      className="col-12"
      isLoading={isLoading}
    />
  )

  return (
    <PanelContainer
      header={getConfigTranslation(config.orderResponse.orderInformation.title)}
      id="order-information"
      visible={config.orderResponse.orderInformation.enabled}
    >
      <>
        <div className="formgrid grid">
          {/* orderId */}
          {orderIdWrapper}

          {/* orderDate */}
          {orderDateWrapper}

          {/* orderDeliveryDate */}
          {orderDeliveryDateWrapper}

          {/* termsOfDelivery */}
          {termsOfDeliveryWrapper}

          {/* currency */}
          {currencyWrapper}

          {/* totalNetItemAmount */}
          {totalNetItemAmountWrapper}

          {/* notes */}
          {notesWrapper}
        </div>

        {/* allowanceCharges */}
        {config.orderResponse.orderInformation.allowanceCharges.enabled !==
          false && <AllowanceCharges id={id} />}

        {/* termsOfPayment */}
        {config.orderResponse.orderInformation.termsOfPayment.enabled !==
          false && <TermsOfPayment id={id} />}
      </>
    </PanelContainer>
  )
}
